import React from "react";
import { PhoneIncoming, PhoneOutgoing, ArrowLeftRight } from "lucide-react";
import { StatusBadge, NeedsCallbackBadge, MotifTag } from "@/components/CallList";
import { CallbackButton } from "@/components/CallFlow";
import { LineBadge } from "@/components/LineBadge";
import { formatCallTime, formatCallDuration, initials } from "@/lib/format";

const DIR_ICON = { inbound: PhoneIncoming, outbound: PhoneOutgoing, internal: ArrowLeftRight };
const DIR_COLOR = { inbound: "text-indigo-500", outbound: "text-blue-500", internal: "text-slate-400" };

const CallCard = ({ call, onSelect }) => {
  const DirIcon = DIR_ICON[call.direction] || ArrowLeftRight;
  return (
    <button
      type="button"
      data-testid={`call-card-${call.id}`}
      onClick={() => onSelect(call)}
      className="w-full text-left rounded-xl border border-slate-200 bg-white shadow-sm active:bg-slate-50 transition-colors p-3 flex items-center gap-3"
    >
      <span className="h-10 w-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-xs font-semibold text-slate-500 shrink-0">
        {initials(call.name)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <DirIcon className={`h-3.5 w-3.5 shrink-0 ${DIR_COLOR[call.direction] || "text-slate-400"}`} strokeWidth={2.4} />
            <p className="font-semibold text-slate-900 text-sm truncate">{call.name}</p>
          </div>
          <span className="text-xs text-slate-400 tabular-nums shrink-0">{formatCallTime(call.started_at)}</span>
        </div>
        <div className="flex items-center justify-between gap-2 mt-1">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xs text-slate-400 tabular-nums truncate">{call.number}</span>
            <LineBadge line={call.line} compact testid={`line-badge-card-${call.id}`} />
            <StatusBadge status={call.status} />
          </div>
          <span className="text-xs text-slate-500 tabular-nums shrink-0">{formatCallDuration(call)}</span>
        </div>
      </div>
    </button>
  );
};

export const CallCards = ({ calls, onSelect }) => (
  <div className="space-y-2" data-testid="calls-cards">
    {calls.map((c) => (
      <CallCard key={c.id} call={c} onSelect={onSelect} />
    ))}
  </div>
);

// Grouped mobile cards: one card per phone number, most recent call on top.
const GroupedCallCard = ({ row, onSelect }) => {
  const DirIcon = DIR_ICON[row.last_call.direction] || ArrowLeftRight;
  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={`group-card-${row.number_key}`}
      onClick={() => onSelect(row)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(row); } }}
      className="w-full text-left rounded-xl border border-slate-200 bg-white shadow-sm active:bg-slate-50 transition-colors p-3 flex items-center gap-3 cursor-pointer"
    >
      <span className="h-10 w-10 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-xs font-semibold text-slate-500 shrink-0">
        {initials(row.name)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 min-w-0">
            <DirIcon className={`h-3.5 w-3.5 shrink-0 ${DIR_COLOR[row.last_call.direction] || "text-slate-400"}`} strokeWidth={2.4} />
            <p className="font-semibold text-slate-900 text-sm truncate">
              {row.name}
              {row.call_count > 1 && <span className="text-slate-400 font-normal"> ({row.call_count})</span>}
            </p>
          </div>
          <span className="text-xs text-slate-400 tabular-nums shrink-0">{formatCallTime(row.last_call.started_at)}</span>
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          {row.motif && <MotifTag motif={row.motif} />}
          <span className="text-xs text-slate-400 tabular-nums">{row.number}</span>
          <LineBadge line={row.last_call?.line} compact testid={`line-badge-m-${row.number_key}`} />
          <StatusBadge status={row.last_call.status} />
          {row.needs_callback && <NeedsCallbackBadge />}
          {row.needs_callback && (
            <CallbackButton numberKey={row.number_key} number={row.number} name={row.name} line={row.last_call?.line} testid={`callback-btn-m-${row.number_key}`} />
          )}
        </div>
      </div>
    </div>
  );
};

export const GroupedCallCards = ({ rows, onSelect }) => (
  <div className="space-y-2" data-testid="calls-cards">
    {rows.map((row) => (
      <GroupedCallCard key={row.number_key} row={row} onSelect={onSelect} />
    ))}
  </div>
);
